import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { SearchIcon, RepeatIcon, FingerprintIcon, ScanSearchIcon, UsersIcon, CompassIcon } from 'lucide-react';
const GOLD = '#d8b878';
const POINTS = [{
  icon: SearchIcon,
  text: 'Identificar pensamientos y creencias que han frenado tu evolución.'
}, {
  icon: RepeatIcon,
  text: 'Reconocer patrones mentales y emocionales que te mantienen en automático.'
}, {
  icon: FingerprintIcon,
  text: 'Cuestionar ideas heredadas que has confundido con tu identidad.'
}, {
  icon: ScanSearchIcon,
  text: 'Observar con más honestidad tus decisiones, relaciones y miedos.'
}, {
  icon: UsersIcon,
  text: 'Comprender tus relaciones como espejos de conciencia.'
}, {
  icon: CompassIcon,
  text: 'Recuperar más claridad, presencia y dirección interior.'
}];
export function Benefits() {
  const container = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  const item = {
    hidden: {
      opacity: 0,
      y: 18
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };
  return <section className="relative overflow-hidden bg-matrix-dark py-20 md:py-28 px-5 md:px-8" data-id="element-15">
      {/* Depth: black→green bruma + soft perspective grid, not a flat curtain */}
      <div className="pointer-events-none absolute inset-0 z-0 bg-[radial-gradient(ellipse_at_top,_rgba(0,255,65,0.08)_0%,_transparent_58%)]" data-id="element-16"></div>
      <div className="pointer-events-none absolute bottom-0 left-1/2 z-0 h-[45%] w-[150%] -translate-x-1/2 opacity-[0.10]" style={{
      transform: 'translateX(-50%) perspective(500px) rotateX(64deg)',
      transformOrigin: 'bottom center',
      backgroundImage: 'linear-gradient(rgba(216,184,120,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,255,65,0.4) 1px, transparent 1px)',
      backgroundSize: '54px 54px',
      maskImage: 'linear-gradient(to top, black 0%, transparent 80%)',
      WebkitMaskImage: 'linear-gradient(to top, black 0%, transparent 80%)'
    }} data-id="element-17"></div>
      {/* Smooth transitions in/out so the green block doesn't hard-cut */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-0 h-24 bg-gradient-to-b from-matrix-black to-transparent" data-id="element-18"></div>
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-24 bg-gradient-to-t from-matrix-black to-transparent" data-id="element-19"></div>

      <div className="max-w-4xl mx-auto relative z-10" data-id="element-20">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.8
      }} className="text-center mb-12 md:mb-16 max-w-2xl mx-auto" data-id="element-21">
          <span className="inline-block font-inter text-[10px] md:text-xs tracking-[0.4em] uppercase mb-5" style={{
          color: 'rgba(216,184,120,0.7)'
        }} data-id="element-22">
            Lo que esta lectura puede abrir en ti
          </span>
          <h2 className="font-cinzel text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-matrix-cream mb-6 leading-[1.2] drop-shadow-[0_0_14px_rgba(245,230,200,0.13)]" data-id="element-23">
            Al leer este ebook, podrías empezar a ver tu vida con más claridad
          </h2>
          <p className="text-[15px] md:text-lg text-matrix-white/80 font-light leading-[1.9]" data-id="element-24">
            Esta lectura no busca darte fórmulas vacías. Busca acompañarte a
            observar con más claridad lo que antes operaba en silencio dentro de
            ti, y abrir una mirada más consciente sobre tu historia, tus
            patrones y tu forma de vivir.
          </p>
        </motion.div>

        {/* Refined editorial list — activated lines, not heavy boxes */}
        <motion.ul variants={container} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-50px'
      }} className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-1" data-id="element-25">
          {POINTS.map(({
          icon: Icon,
          text
        }, i) => <motion.li key={i} variants={item} className="group relative flex items-start gap-4 py-4 md:py-5 border-b border-matrix-green/12" data-id="element-26">
              {/* Activated left line on hover/reveal */}
              <motion.span className="absolute left-0 top-1/2 h-0 w-px -translate-y-1/2" style={{
            background: 'linear-gradient(180deg, transparent, rgba(216,184,120,0.7), transparent)'
          }} animate={{
            height: ['0%', '70%', '0%'],
            opacity: [0, 0.8, 0]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: i * 0.3
          }} data-id="element-27" />
              <span className="shrink-0 mt-0.5 flex h-8 w-8 items-center justify-center rounded-md border border-[rgba(216,184,120,0.22)] bg-matrix-green/[0.05]" style={{
            color: GOLD
          }} data-id="element-28">
                <Icon className="w-[17px] h-[17px]" strokeWidth={1.5} data-id="element-29" />
              </span>
              <p className="text-matrix-white/85 text-[14.5px] md:text-base font-light leading-snug md:leading-relaxed" data-id="element-30">
                {text}
              </p>
            </motion.li>)}
        </motion.ul>

        {/* Closing phrase */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.9,
        delay: 0.2
      }} className="mt-14 md:mt-20 mx-auto max-w-3xl text-center" data-id="element-31">
          <div className="w-px h-10 bg-gradient-to-b from-transparent via-[rgba(216,184,120,0.5)] to-transparent mx-auto mb-8" data-id="element-32"></div>
          <p className="font-cinzel text-xl sm:text-2xl md:text-3xl leading-[1.5] text-matrix-cream" style={{
          textShadow: '0 0 26px rgba(216,184,120,0.2)'
        }} data-id="element-33">
            No se trata de escapar del mundo. Se trata de{' '}
            <span className="italic" style={{
            color: GOLD,
            textShadow: '0 0 28px rgba(216,184,120,0.45)'
          }} data-id="element-34">
              dejar de vivir dormido
            </span>{' '}
            dentro de él.
          </p>
        </motion.div>
      </div>
    </section>;
}